import { Component, ElementRef, EventEmitter, inject, Input, NgZone, OnDestroy, AfterViewInit, Output, ViewChild } from '@angular/core';
import { ScriptLoaderService } from 'src/app/services/platform/script-loader.service';

declare const grecaptcha: {
  ready(cb: () => void): void;
  render(container: HTMLElement, params: { sitekey: string; callback: (token: string) => void; 'expired-callback': () => void }): number;
  reset(widgetId?: number): void;
};

/** Loaded with the first captcha instead of from index.html (docs/cookie-consent.md). */
const RECAPTCHA_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';

@Component({
  standalone: true,
  selector: 'app-recaptcha',
  template: `<div #container></div>`,
})
export class RecaptchaComponent implements AfterViewInit, OnDestroy {
  @Input({ required: true }) siteKey!: string;
  @ViewChild('container') private readonly container!: ElementRef<HTMLElement>;

  @Output() readonly resolved = new EventEmitter<string | null>();

  private readonly ngZone = inject(NgZone);
  private readonly scriptLoader = inject(ScriptLoaderService);
  private widgetId: number | undefined;
  private destroyed = false;

  ngAfterViewInit(): void {
    const loaded = (globalThis as { grecaptcha?: unknown }).grecaptcha === undefined ? this.scriptLoader.load(RECAPTCHA_SRC) : Promise.resolve();
    loaded.then(() => {
      if (this.destroyed) return;
      grecaptcha.ready(() => this.renderWidget());
    }, () => { });
  }

  private renderWidget(): void {
    if (this.destroyed) return;
    this.widgetId = grecaptcha.render(this.container.nativeElement, {
      sitekey: this.siteKey,
      callback: (token) => this.emitResolved(token),
      'expired-callback': () => this.emitResolved(null),
    });
  }

  private emitResolved(token: string | null): void {
    this.ngZone.run(() => this.resolved.emit(token));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.widgetId !== undefined) {
      grecaptcha.reset(this.widgetId);
    }
  }
}
